import { CSSProperties } from 'react';
import { makeAutoObservable } from 'mobx';
import { environment } from '@environment';
import { SpecialistUserTypes } from 'core/constants/specialist-user-types';
import { AppLoadStep, CommonStep } from 'core/enums/analytics-journey-key';
import { FeatureFlag } from 'core/enums/feature-flag';
import { Feature } from 'core/enums/features';
import { SitePlatform } from 'core/enums/site-platform';
import { OnDemandDetailsDTO } from 'core/models/account/on-demand';
import { BaseRoute } from 'core/models/base-routes';
import { HaptikBody } from 'core/models/haptik';
import { AccountMahService } from 'core/services/api/account-mah';
import { OnDemandService } from 'core/services/api/actions/on-demand';
import { PremiumRewardsService } from 'core/services/api/premium-rewards';
import { ProfileService } from 'core/services/api/profile';
import { HttpClient } from 'core/services/http';
import { StorageHelper } from 'core/services/storage';
import { UmbracoConfigService } from 'core/services/umbraco/config';
import { AnalyticsEventStore } from 'core/stores/analytics-events.store';
import { AnalyticsStructureStore } from 'core/stores/analytics-structure.store';
import { AuthUtils } from 'core/stores/auth/auth-utils';
import { AuthStore } from 'core/stores/auth/auth.store';
import { CurrencyStore } from 'core/stores/currency.store';
import { FeatureStore } from 'core/stores/feature.store';
import { GenexStore } from 'core/stores/genex.store';
import { HaptikStore } from 'core/stores/haptik.store';
import { PaymentPlatform, PaymentStore } from 'core/stores/payment/payment.store';
import { FriendlyPlatformNameMap, SiteConfigStore } from 'core/stores/site-config.store';
import { InboxStore } from 'inbox/inbox.store';
import { routes as ROUTES } from './app.routes';
import RecurringPaymentStore from './core/stores/payment/recurring-payment.store';
import RecurringPaymentStoreManager from './core/stores/payment/recurring-payment.store.manager';
import { Util } from './utils/common.utils';
import i18n from '../i18n/i18n';

export class AppStore {
  title = 'DStv Self Service';
  background: string | null;
  backgroundOptions!: {
    grayscale?: boolean;
    additionalOptions?: {
      backgroundPosition?: string;
      backgroundAttachment?: string;
      backgroundSize?: string;
    };
    opacity?: number;
    isDefault?: boolean;
    bgContainerCssProps?: CSSProperties;
  };
  defaultBackground!: string;
  locale = localStorage.getItem('ci.app-culture') || environment.DEFAULT_LOCALE || 'en-za';
  currencyCode!: string;
  availableRoutes: BaseRoute[] = [];
  menuItems: BaseRoute[] = [];
  authStore: AuthStore;
  featureStore!: FeatureStore;
  paymentStore = new PaymentStore();
  recurringPaymentStore = new RecurringPaymentStore();
  currencyStore!: CurrencyStore;
  inboxStore = new InboxStore();
  haptikStore!: HaptikStore;
  routes!: { menuItems: BaseRoute[]; availableRoutes: BaseRoute[] };
  pathname!: string;
  accountNumber!: number;
  inboxDisplay = {
    display: false,
    unread: 0,
    url: ``,
  };

  activeRoute!: BaseRoute;

  isAuthLoading = true;
  isAccountLoading = false;
  isSubscriptionsLoading = true;
  isOnDemandLoading = false;
  isFeatureLoading = true;
  isConfigLoading = false;
  paymentInProgress = false;
  showWelcome = false;
  init = false;

  premiumStatusIsLoading = false;
  hasPremium = false;
  hasSubscriptions = false;
  hasBoxOffice = false;

  openLiveChat = false;

  dynamicMenuHeight = false;

  // Config from Umbraco
  countryName!: string;
  countryCode!: string;
  countryCodeThreeChar!: string;

  // NOTE: this will hide the side menu, and change the top header
  alternativeLayout = false;

  // NOTE: Needed to block Specialist payments
  isSpecialistCustomer = false;
  // NOTE: Needed to block Lesotho payments
  isLesotho = false;

  get platform() {
    return SiteConfigStore.platform();
  }

  get intendedPlatform() {
    return SiteConfigStore.intendedPlatform();
  }

  get platformMissMatch() {
    return SiteConfigStore.platformMissMatch();
  }

  get showBrandMissMatchPopup() {
    return (
      !this.isLoading && this.featureStore.hasViewPermission(FeatureFlag.BrandMissMatchPopup) && this.platformMissMatch
    );
  }

  get showWelcomePopup() {
    return (
      this.featureStore &&
      !this.featureStore.isLoading &&
      this.featureStore.hasViewPermission(FeatureFlag.WelcomePopup) &&
      this.showWelcome
    );
  }

  get showTopMenu() {
    return (
      !this.alternativeLayout &&
      !this.isFeatureLoading
    );
  }

  get showSideMenu() {
    return (
      !this.alternativeLayout
    );
  }

  get showLiveChat() {
    return (
      this.featureStore &&
      !this.featureStore.isLoading &&
      this.featureStore.hasViewPermission(FeatureFlag.LiveChat) &&
      this.isAuthenticated
    );
  }

  changeAlternativeLayout = (alternativeLayout: boolean) => {
    this.alternativeLayout = alternativeLayout;
  };

  get isAuthorized() {
    return this.authStore.isAuthorized;
  }

  get appEntryUrl() {
    return this.authStore.appEntryUrl;
  }

  get customerNumber() {
    return this.authStore.customerNumber;
  }

  get isAuthenticated() {
    return this.authStore.isAuthenticated;
  }

  get lang() {
    return this.locale?.split('-')[0] || 'en';
  }

  get country() {
    return this.authStore.countryCode || this.locale?.split('-')[1] || 'za';
  }

  get currency() {
    return this.currencyStore.currency;
  }

  get menuConfig() {
    const site = this.intendedPlatform === SitePlatform.gotv ? 'gotv' : '';
    const culture = `${this.lang}-${this.country}`;

    return this.featureStore?.hasViewPermission(FeatureFlag.UserBasedMenuConfig)
      ? { site, culture }
      : undefined;
  }

  get isLoading() {
    return (
      !this.isCoreReady ||
      !this.featureStore ||
      this.featureStore?.isLoading ||
      this.premiumStatusIsLoading ||
      this.isOnDemandLoading ||
      !AnalyticsStructureStore.hasStructureDataLoaded
    );
  }

  get isCoreReady() {
    return (
      !this.authStore.isLoading &&
      !this.isConfigLoading &&
      !this.isFeatureLoading &&
      !this.isAccountLoading
    )
  }

  get ready() {
    return (
      this.paymentStore.init &&
      this.isCoreReady &&
      this.isAuthenticated
    )
  }

  get features() {
    return this.featureStore?.permissionMap;
  }

  constructor() {
    this.authStore = new AuthStore();
    this.background = null;

    this.haptikStore = new HaptikStore();

    localStorage.setItem('ci.app-culture', Util.LocaleFromUrl()); // NOTE: used for login redirect-uri

    SiteConfigStore.initialize().changeUrlBasedPlatform(Util.GetConfigFromURL().platform);

    makeAutoObservable(this);
  }

  initialize(featureStore: FeatureStore) {
    if (!this.init) {
      this.init = true;
      this.changeIsAuthLoading(true);
      this.changeIsFeatureLoading(true);
      this.changeIsSubscriptionsLoading(true);
      this.featureStore = featureStore;
      HttpClient.initialize(this);
      AnalyticsStructureStore.initialize();

      this.authStore.initialize(this.locale)?.then(
        (user) => {
          this.changeCountry(this.authStore.countryCode);

          this.changeIsAuthLoading(false);

          this.changeIsConfigLoading(true);
          this.changeIsAccountLoading(true);

          // TODO: Clean-up init configuration logic
          Promise.all([
            this.featureStore.initialize(this.country, this.platform),
            UmbracoConfigService.getSiteConfig(this.country),
          ])?.then(([_, config]) => {
            this.countryName = config.CountryName;
            this.countryCode = config.CountryCode;
            this.countryCodeThreeChar = config.AlternateCountryCode;

            this.changeCurrencyStore(
              new CurrencyStore().initialise(this.countryName, config, true, this.onCurrencyChange)
            );

            SiteConfigStore.changeAllowCrossPlatform(featureStore.hasViewPermission(FeatureFlag.AllowGotvInterface));

            if (this.isAuthorized) {
              if (featureStore.getHaptikFlag()) {
                this.haptikStore.onInit();

                const haptikBodyObject: HaptikBody = {
                  idToken: AuthUtils.GetStoredToken() ?? '',
                  connectId: AuthUtils.GetStoredConnectId() ?? '',
                  agreementId: AuthUtils.GetStoredAgreementId() ?? '',
                  mobileNumber: AuthUtils.GetStoredMobileNumber() ?? '',
                  authenticated: this.isAuthorized,
                  countryCode: this.countryCode ?? '',
                  customerNumber: this.customerNumber ?? '',
                  email: this.authStore.emailAddress ?? '',
                };
                this.haptikStore.loadSignInBot(haptikBodyObject);
              }

              if (!StorageHelper.GetInstance.cookieExistsAndHasValue('dstv-ss.visited', `true`)) {
                this.changeShowWelcome(true);
                StorageHelper.GetInstance.setCookie('dstv-ss.visited', `true`, 730);
              }
            }

            if (featureStore.getGenexPlatform()) {
              GenexStore.initialize(featureStore.getGenexPlatform(), this.authStore.emailAddress, this.lang);
            }

            this.changeIsConfigLoading(false);
            this.changeIsFeatureLoading(false);

            if (!this.isAuthenticated) {
              this.changeRoutes(this.setRoutes());
              this.changeIsAccountLoading(false);
              return;
            }

            const userLocale = `${this.lang}-${this.country}`;
            if (this.featureStore?.hasViewPermission(FeatureFlag.UserBasedUrlLocale) && Util.LocaleFromUrl() !== userLocale) {
              const url = window.location.href.replace(`/${Util.LocaleFromUrl()}/`, `/${userLocale}/`);
              window.history.replaceState({}, "", url);
            }

            if (featureStore.hasViewPermission(FeatureFlag.InboxRoute)) {
              this.inboxStore.initialize(this);
              this.inboxStore.startPoll();
            }

            GenexStore.saveUserData({
              country: this.countryName,
              countryCode: this.countryCode,
            });

            ProfileService.getProfile(this.customerNumber, this.countryName)
              .then((profileUser) => {
                AnalyticsEventStore.initialize(
                  profileUser,
                  this.locale,
                  this.customerNumber,
                  this.countryName,
                  this.countryCodeThreeChar
                );

                this.changeIsLesotho(!!profileUser.listOfAddress?.some((address) => address.province === 'Lesotho'));

                GenexStore.saveUserData({
                  ...GenexStore.optionalUserData,
                  phoneNumber: profileUser?.listOfPhoneNumber?.[0]?.number,
                  userName: `${profileUser.title} ${profileUser?.firstName} ${profileUser?.surname}`,
                });

                if (
                  profileUser.customerClass.toLowerCase() === 'specialist' &&
                  SpecialistUserTypes.includes(profileUser.customerType.toLowerCase())
                ) {
                  this.changeIsSpecialistCustomer(true);
                }
              })
              .catch((error) => {
                console.error('Failed to retrieve user profile.', error);
              });


            RecurringPaymentStoreManager.initializeFromAppContext(this);

            AccountMahService.getAmountToPay(this.customerNumber, this.countryName)
              .then((amountToPay) => {
                const accounts = amountToPay?.accounts || [];

                SiteConfigStore.changeUserBasedPlatform(SiteConfigStore.getPlatform(accounts));

                const account = SiteConfigStore.getAccount(accounts);

                const platformOverride = localStorage.getItem('ci.payment-platform-override') as PaymentPlatform | null;
                this.paymentStore.initialize(
                  this.countryName,
                  this.countryCodeThreeChar,
                  this.customerNumber,
                  this.currencyStore,
                  this.lang,
                  platformOverride || featureStore.getPaymentPlatform()
                );

                if (!account) {
                  this.changeRoutes(this.setRoutes());
                  this.changeIsAccountLoading(false);
                  console.error('No Customer Account Found');
                  return;
                }

                this.changeAccountNumber(account.accountNumber);

                this.checkPremiumAccount();

                this.changeIsAccountLoading(false);
              });

            const boxofficeFeature = featureStore.hasViewPermission(FeatureFlag.BoxOfficeRoute);

            if (boxofficeFeature) {
              this.changeIsOnDemandLoading(true);

              (boxofficeFeature
                  ? OnDemandService.getBalance(this.customerNumber, this.countryName)
                  : Promise.resolve(null)
              ).then(
                (boxOffice) => {
                  if (boxOffice) {
                    this.changeHasBoxOffice(boxOffice);
                  }
                  this.changeIsOnDemandLoading(false);
                },
                (err) => {
                  console.error('Failed to get amount to pay / boxOffice -> ', err);
                  this.changeRoutes(this.setRoutes());
                  this.changeIsOnDemandLoading(false);
                }
              );

              this.changeIsFeatureLoading(false);
              return;
            }

            this.changeRoutes(this.setRoutes());
          });
        },
        (error) => {
          console.error('Initialization failed at CMS -> ', error);
        }
      );
    }
  }

  checkPremiumAccount() {
    const premiumFeature =
      this.featureStore.hasViewPermission(FeatureFlag.RewardsPremiumStatusRoute) ||
      this.featureStore.hasViewPermission(FeatureFlag.RewardsWhatIsPremiumRoute);

    if (!premiumFeature || !this.accountNumber) {
      return;
    }

    this.changePremiumStatusIsLoading(true);

    PremiumRewardsService.getPremiumStatus(this.customerNumber, this.countryName, `${this.accountNumber}`).then(
      (statuses) => {
        this.changeHasSubscriptions(true);
        this.changeHasPremium(!!statuses);
        this.changePremiumStatusIsLoading(false);
        this.changeRoutes(this.setRoutes());
      },
      (_) => {
        this.changeHasPremium(false);
        this.changePremiumStatusIsLoading(false);
        this.changeRoutes(this.setRoutes());
      }
    );
  }

  changeTitle(title: string) {
    this.title = `${FriendlyPlatformNameMap[this.platform]} - ${title}`;
  }

  changeOpenLiveChat(openLiveChat: boolean) {
    this.openLiveChat = openLiveChat;
  }

  changePathname(pathname: string) {
    if (!pathname) {
      return;
    }
    this.pathname = pathname;
  }

  changeRoutes(routes: { menuItems: BaseRoute[]; availableRoutes: BaseRoute[] }) {
    this.routes = routes;
  }

  changeActiveRoute(name: string | null) {
    this.activeRoute = this.routes.availableRoutes.find((r) => r.name === name) as BaseRoute;
    const parent = this.activeRoute?.parent?.name;
    this.routes = {
      availableRoutes: this.routes.availableRoutes,
      menuItems: this.routes.menuItems.map((route) => ({
        ...route,
        isActive: parent === route.name || route.name === name,
      })),
    };
  }

  changeIsFeatureLoading(isLoading: boolean) {
    this.isFeatureLoading = isLoading;
  }

  changeIsAuthLoading(isLoading: boolean) {
    this.isAuthLoading = isLoading;
  }

  changeIsSubscriptionsLoading(isLoading: boolean) {
    this.isSubscriptionsLoading = isLoading;
  }

  changeIsAccountLoading(isAccountLoading: boolean) {
    this.isAccountLoading = isAccountLoading;
  }

  changeIsConfigLoading(isConfigLoading: boolean) {
    this.isConfigLoading = isConfigLoading;
  }

  changePaymentPlatform(paymentPlatform: PaymentPlatform | null) {
    const platform = paymentPlatform || this.featureStore.getPaymentPlatform();
    this.paymentStore.initialize(this.countryName, this.countryCodeThreeChar, this.customerNumber, this.currencyStore, this.lang, platform);
  }

  changeCountry = (country: string | null) => {
    const lang = this.locale?.split('-')[0] || 'en';
    if (!country) {
      return;
    }

    this.changeLocale(`${lang}-${country}`);
  };

  changeLocale = (locale = '') => {
    this.locale = locale.toLowerCase();
    i18n.changeLanguage(this.locale);
  };

  onCurrencyChange = (currency = '') => {
    this.currencyCode = currency;
    this.paymentStore.changeCurrencyCode(currency);
  };

  changeCurrencyStore(currencyStore: CurrencyStore) {
    this.currencyStore = currencyStore;
  }

  changePaymentInProgress = (progress: boolean) => {
    this.paymentInProgress = progress;
  }

  changeShowWelcome(showWelcome: boolean) {
    this.showWelcome = showWelcome;
  }

  changeInboxDisplay(inbox: any) {
    this.inboxDisplay = inbox;
  }

  changeAvailableRoutes(routes: BaseRoute[]) {
    this.availableRoutes = routes;
  }

  changeMenuItems(routes: BaseRoute[]) {
    this.menuItems = routes;
  }

  changeAccountNumber(accountNumber: number) {
    this.accountNumber = accountNumber;
  }

  canActivate(isProtected?: boolean | 'Authorize') {
    return isProtected === 'Authorize'
      ? () => this.isAuthorized
      : isProtected === true
        ? () => this.isAuthenticated
        : undefined;
  }

  canViewInMenu(route: BaseRoute) {
    if (
      this.featureStore.hasViewPermission(FeatureFlag.RewardsPremiumStatusRoute) &&
      route.feature === Feature.RewardsPremiumStatus
    ) {
      return this.hasPremium;
    }
    if (
      this.featureStore.hasViewPermission(FeatureFlag.RewardsWhatIsPremiumRoute) &&
      route.feature === Feature.RewardsWhatIsPremium
    ) {
      return !this.hasPremium;
    }
    // NOTE: Added as a part of the field trial cleanup - hide box office route if the user doesn't have a box office account,
    // once we get a functionality for users to get Box Office, this will be changed
    if (route.feature === Feature.BoxOffice) {
      return this.hasBoxOffice;
    }
    return true;
  }

  setRoutes() {
    return ROUTES.reduce(
      (prev: { menuItems: BaseRoute[]; availableRoutes: BaseRoute[] }, route: BaseRoute) => {
        const { children, ...baseRoute } = route;
        const childRoutes =
          children
            ?.filter((child) => this.featureStore?.hasRoutePermission(child.feature))
            .map((child) => ({
              ...child,
              parent: baseRoute,
              canActivate: this.canActivate(route.protected || baseRoute.protected),
            })) || [];
        const menuChildRoutes =
          children
            ?.filter(
              (child) =>
                this.featureStore?.hasRoutePermission(child.feature) &&
                !child.hideFromMainMenu &&
                this.canViewInMenu(child)
            )
            .map((child) => ({
              ...child,
              parent: baseRoute,
              canActivate: this.canActivate(route.protected || baseRoute.protected),
            })) || [];
        const routeWithPermission = this.featureStore?.hasRoutePermission(route.feature)
          ? [{ ...route, children: menuChildRoutes, canActivate: this.canActivate(route.protected) }]
          : [];

        return {
          availableRoutes: [...prev.availableRoutes, ...routeWithPermission, ...childRoutes],
          menuItems: [...prev.menuItems, ...routeWithPermission], // .filter(route => this.canViewInMainMenu(route))
        };
      },
      { menuItems: [], availableRoutes: [] }
    );
  }

  processPayment(transactionId: string, responseType: string, type?: string) {
    if (responseType === 'back') {
      this.paymentStore.paymentFailed('Not completed');
    }
    if (responseType === 'success') {
      this.paymentStore.startPoll(transactionId, type || '2');
    }
    if (responseType === 'failure') {
      this.paymentStore.paymentFailed('Failed response');
    }
  }

  changePremiumStatusIsLoading(value: boolean) {
    this.premiumStatusIsLoading = value;
  }

  changeHasPremium(value: boolean) {
    this.hasPremium = value;
  }

  changeHasSubscriptions(value: boolean) {
    this.hasSubscriptions = value;
  }

  changeHasBoxOffice(onDemandDetailsDTO: OnDemandDetailsDTO) {
    this.hasBoxOffice =
      !!onDemandDetailsDTO.accounts.length &&
      !!onDemandDetailsDTO.accounts.find((account) => account.accountType === 'Box Office');
  }

  changeIsOnDemandLoading(isLoading: boolean) {
    this.isOnDemandLoading = isLoading;
  }

  changeIsSpecialistCustomer(isSpecialistCustomer: boolean) {
    this.isSpecialistCustomer = isSpecialistCustomer;
  }

  reInitAuth() {
    return this.authStore.initialize(this.locale, true);
  }

  changeIsLesotho(isLesotho: boolean) {
    return (this.isLesotho = isLesotho);
  }

  changeDynamicMenuHeight(isDynamic: boolean) {
    this.dynamicMenuHeight = isDynamic;
  }
}